import { CSSProperties, FC, ReactNode, useRef } from 'react';
import './DropDownMenu.scss';
import classNames from 'classnames';
import { Popper } from '../components/Popper';
import { Placement } from '@popperjs/core';
import { LoanFormatIcon } from './GildIcon';

type DropDownProps = {
    readonly className?: string;
    readonly iconCode?: string;
    readonly iconClass?: string;
    readonly iconSizeClass?: string;
    readonly iconDefaultClasses?: string;
    readonly btnClassName?: string;
    readonly btnInnerClassName?: string;
    readonly ddInnerContainerClass?: string;
    readonly labelClassName?: string;
    readonly label?: ReactNode;
    readonly htmlLabel?: ReactNode;
    readonly title?: string;
    readonly dropUp?: boolean;
    readonly showActiveArrow?: boolean;
    readonly arrowStyle?: CSSProperties;
    readonly arrowClassName?: string;
    readonly dropdownFooter?: ReactNode;
    readonly onClick?: () => void;
    readonly onOpenListener?: () => void;
    readonly onCloseListener?: () => void;
    readonly dataDisplayname?: string;
    readonly showAsButton?: boolean;
    readonly children: ReactNode;
};

type Props = DropDownProps & {
    readonly expanded: boolean;
    readonly align?: 'left' | 'center' | 'right';
    readonly gap?: number;
    readonly displacement?: number;
    readonly onToggleClick: (expanded: boolean) => void;
    readonly onClickOutside: (expanded: boolean) => void;
    readonly arrowIcon?: string;
    readonly children?: ReactNode;
};

function getLabelIcon(
    iconCode: string | undefined,
    iconDefaultClasses: string | undefined,
    iconSizeClass: string | undefined
) {
    if (!iconCode) {
        return null;
    }

    // iconSizeClass = iconSizeClass || 'normal';

    if (
        iconCode === 'emagazine-filled' ||
        iconCode === 'ebook-filled' ||
        iconCode === 'eaudiobook-filled' ||
        iconCode === 'star-outline'
    ) {
        return <LoanFormatIcon icon={iconCode} className={classNames('loanformat-icon-large')} />;
    } else {
        return <i className={classNames(iconDefaultClasses ?? 'material-icons fl-left', iconSizeClass)}>{iconCode}</i>;
    }
}

/**
 * Drop-In replacement for <DropDown>.
 * It is intended to gradually replace <DropDown>, such that the awful <GenericDropDown> can be removed soon.
 */
export const DropDownMenu: FC<Props> = props => {
    const toggleRef = useRef<HTMLAnchorElement>(null);
    const arrowRef = useRef<HTMLSpanElement>(null);
    const scrollInnerRef = useRef<HTMLUListElement>(null);

    const applyDefaultFloating = !(props.className && props.className.includes('fl-'));
    const icon = getLabelIcon(props.iconCode, props.iconDefaultClasses, props.iconSizeClass);
    const labelClass = classNames(props.btnInnerClassName, props.iconClass, 'gild');
    const ddInnerContainer = classNames('default-dd', props.ddInnerContainerClass);

    const label = props.label ? (
        <div className={labelClass} style={{ display: 'inline-block' }}>
            {icon}
            <span className={props.labelClassName}>{props.htmlLabel ? props.htmlLabel : props.label}</span>
        </div>
    ) : (
        icon
    );

    const getArrowIcon = (): string => {
        if (props.arrowIcon) {
            return props.arrowIcon;
        }

        if (props.expanded) {
            return props.dropUp ? 'expand_more' : 'expand_less';
        } else {
            return props.dropUp ? 'expand_less' : 'expand_more';
        }
    };

    const arrowIcon = getArrowIcon();

    const placement: Placement =
        props.align === 'left' ? 'bottom-start' : props.align === 'right' ? 'bottom-end' : 'bottom';

    return (
        <div
            className={classNames('dropdown-menu', 'default-dd-nav', props.className, {
                'fl-left': applyDefaultFloating,
                active: props.expanded
            })}
        >
            <a
                data-testid={`dropdownToggle-${props.label}`}
                ref={toggleRef}
                className={classNames('bt-dd', props.btnClassName)}
                title={props.title}
                onClick={() => {
                    if (props.onToggleClick) {
                        props.onToggleClick(!props.expanded);
                    }
                }}
            >
                {label}
                <i style={props.arrowStyle} className={classNames('material-icons', props.arrowClassName)}>
                    {arrowIcon}
                </i>
            </a>
            <Popper
                className={ddInnerContainer}
                onClickOutside={props.onClickOutside}
                referenceElement={toggleRef}
                arrowElement={arrowRef}
                visible={props.expanded}
                placement={placement}
                gap={props.gap}
                displacement={props.displacement}
                portal={false}
            >
                {(props.showActiveArrow ?? true) && <span ref={arrowRef} className="active-arrow" />}
                <ul ref={scrollInnerRef}>{props.children}</ul>
                {props.dropdownFooter}
            </Popper>
        </div>
    );
};
